import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import PageSearchInput from '@/components/Inputs/PageSearchInput';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import TopicResearchCallout from '@/components/Topics/TopicResearchCallout';
import { topicApiSsr } from '@/lib/topicApi';
import { BORDER_RADIUS } from '@/constants/config';
import { colorForSlug, formatCount } from '@/utils/agent_catalog';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 350;

export default function TopicsCategoryPage() {
  const params = useLocalSearchParams();
  const slugRaw = params?.slug;
  const categorySlug = typeof slugRaw === 'string'
    ? slugRaw
    : Array.isArray(slugRaw)
      ? slugRaw[0] ?? ''
      : '';
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isMD, showsScrollIndicator } = useResponsiveness();
  const [topics, setTopics] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery('');
    setDebouncedQuery('');
  }, [categorySlug]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setDebouncedQuery('');
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    if (!categorySlug) return undefined;

    setTopics([]);
    setPagination(null);
    setPage(1);
    setCategoryName('');
    setLoadError(null);
    setLoading(true);

    (async () => {
      try {
        const res = await topicApiSsr.getByCategory(categorySlug, {
          page: 1,
          limit: PAGE_SIZE,
          q: debouncedQuery || undefined,
        });
        if (cancelled) return;
        setTopics(Array.isArray(res?.topics) ? res.topics : []);
        setCategoryName(res?.category?.name || categorySlug);
        setPagination(res?.pagination || null);
      } catch (e) {
        if (!cancelled) {
          setTopics([]);
          setLoadError(e?.message || 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [categorySlug, debouncedQuery]);

  const hasMore = useMemo(() => {
    if (!pagination) return false;
    return page < (pagination.totalPages || 1);
  }, [pagination, page]);

  const loadMore = useCallback(async () => {
    if (!categorySlug || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await topicApiSsr.getByCategory(categorySlug, {
        page: nextPage,
        limit: PAGE_SIZE,
        q: debouncedQuery || undefined,
      });
      const batch = Array.isArray(res?.topics) ? res.topics : [];
      setTopics((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...batch.filter((row) => row?.id && !seen.has(row.id))];
      });
      setPagination(res?.pagination || pagination);
      setPage(nextPage);
    } catch {
      /* keep current list */
    } finally {
      setLoadingMore(false);
    }
  }, [categorySlug, loadingMore, hasMore, page, pagination, debouncedQuery]);

  const accent = useMemo(() => colorForSlug(categorySlug), [categorySlug]);
  const gridCols = isXS ? 1 : isMD ? 3 : 4;
  const totalCount = pagination?.total ?? topics.length;

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    inner: {
      paddingHorizontal: isXS ? 16 : 24,
      paddingTop: 16,
      paddingBottom: 32,
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
      gap: 12,
    },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    card: {
      width: Platform.OS === 'web'
        ? `calc((100% - ${(gridCols - 1) * 10}px) / ${gridCols})`
        : `${100 / gridCols}%`,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: 12,
      gap: 8,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: `${accent}22`,
    },
    loading: { paddingVertical: 48, alignItems: 'center' },
    loadMore: { marginTop: 8, alignSelf: 'center', minWidth: 220 },
    meta: { opacity: 0.7 },
    searchRow: { marginTop: 4 },
  }), [colors, isXS, gridCols, accent]);

  return (
    <View style={styles.container}>
      <MetaHead
        noindex
        title={`${categoryName || categorySlug} · ${t('pages.topics.title')} · Gab AI`}
        description={t('pages.topics.seoMetaFallback')}
        canonical={`/topics/category/${encodeURIComponent(categorySlug)}`}
      />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.inner}>
          <View>
            <Text type="h2">{categoryName || categorySlug}</Text>
            {!loading && pagination?.total != null && (
              <Text type="p" style={styles.meta}>
                {formatCount(totalCount)} {totalCount === 1 ? 'topic' : 'topics'}
              </Text>
            )}
          </View>

          <TopicResearchCallout accent={accent} />

          {!loadError && (
            <PageSearchInput
              value={query}
              onChangeText={setQuery}
              placeholder={t('pages.topics.searchPlaceholder')}
              catalogVariant
              noPadding
              containerStyle={styles.searchRow}
            />
          )}

          {loading ? (
            <View style={styles.loading}>
              <ActivityIndicator color={accent} />
            </View>
          ) : loadError ? (
            <Text type="p" style={{ opacity: 0.75 }}>{t('pages.topics.notFound')}</Text>
          ) : (
            <>
              {topics.length === 0 && debouncedQuery ? (
                <Text type="p" style={{ opacity: 0.75 }}>
                  {t('pages.topics.noResultsForQuery').replace('%QUERY%', debouncedQuery)}
                </Text>
              ) : (
              <View style={styles.grid}>
                {topics.map((row) => (
                  <Pressable
                    key={row.id}
                    style={styles.card}
                    onPress={() => row.slug && router.push(`/start/${encodeURIComponent(row.slug)}`)}
                  >
                    {row.avatarPath ? (
                      <Image source={{ uri: row.avatarPath }} style={styles.avatar} contentFit="cover" />
                    ) : (
                      <View style={styles.avatar} />
                    )}
                    <Text type="p" weight="bold" numberOfLines={2}>{row.name}</Text>
                  </Pressable>
                ))}
              </View>
              )}

              {hasMore && topics.length > 0 && (
                <LoadMoreButton
                  onPress={loadMore}
                  isLoading={loadingMore}
                  isLarge
                  style={styles.loadMore}
                />
              )}
            </>
          )}
        </View>
        <View style={{ paddingHorizontal: isXS ? 16 : 24 }}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
}
